'use client';

import { Button as NextUIButton, ButtonProps } from "@heroui/button";
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import React, { useMemo } from 'react';

interface CustomButtonProps extends Omit<ButtonProps, 'variant'> {
  isTranslated?: boolean;
  href?: string;
  variant?: 'fill' | 'outline' | 'link' | 'ghost' | 'button' | null;
}

const Button = React.forwardRef(
  (
    {
      isTranslated = false,
      href,
      variant = 'fill',
      ...props
    }: CustomButtonProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    const t = useTranslations();
    const content = useMemo(
      () =>
        typeof props.children === 'string'
          ? isTranslated
            ? t(props.children)
            : props.children
          : props.children,
      [props.children, isTranslated]
    );

    return (
      <NextUIButton
        ref={ref}
        {...props}
        {...(href && {
          href,
          as: Link,
        })}
        className={clsx(
          {
            'bg-primary-blue text-white font-poppins font-medium px-6 h-12 !rounded-extra text-[17px] data-[hover=true]:bg-primary-dark-blue transition-background data-[hover=true]:!opacity-100':
              variant === 'fill',
          },
          {
            'border border-primary-blue bg-transparent text-gray-dark font-poppins font-medium px-6 h-12 !rounded-extra text-[17px] data-[hover=true]:text-primary-blue transition-colors data-[hover=true]:!opacity-100':
              variant === 'outline',
          },
          {
            'h-auto text-gray-dark rounded-none data-[hover=true]:text-primary-blue bg-transparent font-poppins font-medium px-0 text-[17px] !transition data-[hover=true]:!opacity-100 aria-[expanded=true]:!opacity-100 aria-[expanded=true]:!scale-y-100 aria-[expanded=true]:!scale-x-100':
              variant === 'link',
          },
          {
            'text-gray-dark data-[hover=true]:text-primary-blue bg-transparent font-poppins font-medium px-0 h-12 text-[17px] !transition-colors data-[hover=true]:!opacity-100 aria-[expanded=true]:!opacity-100 aria-[expanded=true]:!scale-y-100 aria-[expanded=true]:!scale-x-100':
              variant === 'ghost',
          },
          props.className
        )}
      >
        {content}
      </NextUIButton>
    );
  }
);

Button.displayName = 'Button';
export default Button;
